import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { TravelTypes } from '../../../constants';
import { useNLPConstants, TEMP_NUMBER } from '../constants/nlpSettings';
import formatHotelDetailRequest from '../../../helpers/formatHotelDetailRequest';
import { generateHotelIntroMessage } from '../introMessages/hotelIntroMessage';
import getRequest from '../helpers/networkUtil';

const queryString = require('query-string');

/**
 * Utility function generates an URL for getting hotel details.
 * [Used code from]
 * .../HotelInformation/HotelDetails/index.jsx and
 * .../HotelInformation/HotelItinerary/HotelItineraryContent/index.jsx
 * [Note]
 * Using dispatch(requestHotelDetails()) will cause error, so we stopped
 * calling it. If we click to view hotel details on the web page, then click
 * the input box, the Rate Code will disappear. The assumption is that
 * "clicking to view hotel details" will trigger dispatch(requestHotelDetails()),
 * and followed by a Rate Code generation logic. If we click input box to trigger
 * dispatch(requestHotelDetails()), new hotel data will replace the old one, but
 * Rate Code is not generated. So, the Rate Code will disappear.
 */
const useGenerateHotelDetailsUrl = (itinerary, tripId, travelOption) => {
  const { isViewingCompare } = useSelector((store) => store.compare);
  const currencyCode = isViewingCompare ? itinerary.cost.currencyCode : undefined;
  const { currencyCode: currencyCodeStored } = useSelector((store) => store.tripSearch);

  const isTravelOption = travelOption && travelOption.mode === TravelTypes.hotel;

  const { staterooms } = useSelector((store) => store.tripSearch.searchObj);
  const searchObj = useSelector((store) =>
    !isTravelOption
      ? (store.hotel.hotelSearches[tripId] || {}).searchObj
      : store.tripSearch.searchObj.hotel,
  );
  const request = formatHotelDetailRequest(itinerary, staterooms, searchObj);

  const qs = queryString.stringify(
    {
      ...request,
      currencyCode: currencyCode || currencyCodeStored || undefined,
    },
    { skipNull: true },
  );

  // Get the URL
  const url = `/hotels/getHotel?${qs}`;
  return url;
};

/* Self defined hook for extract data and construct system instruction for one hotel
 * based on the input data provided for that hotel */
const useHotelExtractor = (rawData) => {
  const [extractTriggered, setExtractTriggered] = useState(false);
  const [introMessage, setIntroMessage] = useState('');
  const [hotelDetails, setHotelDetails] = useState(null);
  const [error, setError] = useState(null);

  // Get NLP constants
  const nlpConstants = useNLPConstants();
  const HOTEL_EXTRACTOR_TIMEOUT_LIMIT = nlpConstants
    ? parseInt(nlpConstants.HOTEL_EXTRACTOR_TIMEOUT_LIMIT.value, 10)
    : TEMP_NUMBER;

  // Get NLP instructions
  // const nlpInstructions = useNLPInstructions();

  // Read necessary info from the raw data for getting hotel details
  const { itinerary, tripId, travelOption } = rawData;

  // Generate URL for getting hotel details
  const detailsUrl = useGenerateHotelDetailsUrl(itinerary, tripId, travelOption);

  // Request hotel details
  useEffect(() => {
    const dispatchGetHotelDetails = async () => {
      try {
        const details = await getRequest(
          detailsUrl,
          'application/json',
          HOTEL_EXTRACTOR_TIMEOUT_LIMIT,
        );
        setHotelDetails(details);
      } catch (exceptionError) {
        // Set the error state, so it can be accessed in the interface
        console.log(exceptionError);
        setError(exceptionError);
      }
    };

    if (extractTriggered) {
      setExtractTriggered(false);
      dispatchGetHotelDetails();
    }
  }, [extractTriggered, detailsUrl, HOTEL_EXTRACTOR_TIMEOUT_LIMIT]);

  // After getting hotel details, generate the intro message
  useEffect(() => {
    if (hotelDetails && hotelDetails.hotel) {
      const message = generateHotelIntroMessage(hotelDetails, itinerary);
      setIntroMessage(message);
    }
  }, [hotelDetails, itinerary]);

  /* Return both message holder "introMessage", and a trigger. The trigger is for the
   * calling component to start the useHotelExtractor's internal logic. So, in the calling
   * component, we can choose to extract data at a specific time, like input box clicking */
  return {
    introMessage,
    triggerExtract: setExtractTriggered,
    extractorError: error, // Return error state so it can be accessed in the interface
    setExtractorError: setError,
  };
};

export default useHotelExtractor;
